type OnboardingStepStatus = 'completed' | 'skipped';
type StepProgress = {
  completed: Array<number>;
  skipped: Array<number>;
};
export type OnboardingStep = {
  step: number;
  name: string;
  label: string;
  description: string;
};

type GetUserOnboardingApiResponse = {
  onboardingProgress: StepProgress;
  userId: number;
};

const steps = [
  {
    step: 1,
    name: 'onboarding.account-details',
    label: 'onboarding.steps.accountDetails.label',
    description: 'onboarding.steps.accountDetails.description',
  },
  {
    step: 2,
    name: 'onboarding.platform-details',
    label: 'onboarding.steps.platformDetails.label',
    description: 'onboarding.steps.platformDetails.description',
  },
  {
    step: 3,
    name: 'onboarding.company-details',
    label: 'onboarding.steps.companyDetails.label',
    description: 'onboarding.steps.companyDetails.description',
  },
  {
    step: 4,
    name: 'onboarding.add-members',
    label: 'onboarding.steps.addMembers.label',
    description: 'onboarding.steps.addMembers.description',
  },
];

export const useOnboardingStore = defineStore('onboarding', () => {
  const showWelcomeMessage = ref<boolean>(false);
  const onboardingProgress = ref<StepProgress>({ completed: [], skipped: [] });
  const onboardingSteps = ref<Array<OnboardingStep>>([...steps]);

  async function setStepStatus(step: number, status: OnboardingStepStatus) {
    const currentStatusList = onboardingProgress.value[status];
    if (!currentStatusList.includes(step)) currentStatusList.push(step);
    if (status === 'completed') {
      // This is to ensure that a step is not both completed and skipped which can happen if a user completes the onboarding and then fills the platform details by navigating through the status banner
      onboardingProgress.value.skipped = onboardingProgress.value.skipped.filter(s => s !== step);
    }
    const totalSteps = Math.max(...onboardingSteps.value.map(s => s.step));
    await $fetch('/api/onboarding', {
      method: 'PATCH',
      headers: useRequestHeaders(['cookie']),
      body: {
        onboardingProgress: onboardingProgress.value,
      },
    })
      .then(() => {
        if (step === totalSteps) showWelcomeMessage.value = true;
      })
      .catch(error => {
        if (error.data.message) useNotificationBanner(error.data.message, 'error');
        else useNotificationBanner('onboarding.notification.error', 'error');
      });
  }

  async function fetchUserOnboarding() {
    const headers = useRequestHeaders(['cookie']) as Record<string, string>;
    const { data, status } = await useFetch<GetUserOnboardingApiResponse>('/api/onboarding', { headers });

    if (status.value === 'success') {
      onboardingProgress.value = data.value?.onboardingProgress ?? onboardingProgress.value;
    }
  }

  function $reset() {
    showWelcomeMessage.value = false;
    onboardingProgress.value = { completed: [], skipped: [] };
    onboardingSteps.value = [...steps];
  }

  return {
    showWelcomeMessage,
    onboardingSteps,
    onboardingProgress,
    setStepStatus,
    fetchUserOnboarding,
    $reset,
  };
});
